import { FC, useMemo, useState } from 'react';
import { type MenuInfo } from 'rc-menu/lib/interface';

import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { Dropdown, MenuItemType } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { Filter } from '@dynamika-shared/ui/icons';
import {
  NotificationTypes,
  notificationTypesReadable,
} from '@dynamika-shared/types/subscription';

import { MultiFilter } from '@components/NestedFilter/MultiFilter';
import { AllFilterItem } from '@components/NestedFilter/types';

export type SiteRecipientsEventFilterProps = {
  selectedEvents: NotificationTypes[];
  onEventsChange: (events: NotificationTypes[]) => void;
  disabled?: boolean;
};

const EVENT_FILTER_KEY = 'event';
const FILTER_GROUP_KEY = 'site-recipients-filters-group';

const eventItems: AllFilterItem<NotificationTypes>[] = Object.values(
  NotificationTypes
).map(type => ({
  label: notificationTypesReadable[type],
  value: type,
}));

/**
 * "Event Filter" trigger plus the Event chip it puts on screen — the same
 * pattern the Configuration tab uses for its Scope / Recipient Type filters.
 */
export const SiteRecipientsEventFilter: FC<SiteRecipientsEventFilterProps> = ({
  selectedEvents,
  onEventsChange,
  disabled,
}) => {
  const [eventFilterActive, setEventFilterActive] = useState(false);

  const toggleEventFilter = () => {
    if (disabled) {
      return;
    }

    if (eventFilterActive) {
      onEventsChange([]);
    }

    setEventFilterActive(!eventFilterActive);
  };

  const toggleEvent = (value: string) => {
    const event = value as NotificationTypes;

    onEventsChange(
      selectedEvents.includes(event)
        ? selectedEvents.filter(item => item !== event)
        : [...selectedEvents, event]
    );
  };

  const filterMenuItems: MenuItemType[] = useMemo(
    () => [
      {
        key: FILTER_GROUP_KEY,
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: [
          {
            key: EVENT_FILTER_KEY,
            label: (
              <div className='flex justify-between'>
                <span className='text-1424 text-black'>Event</span>

                <Checkbox checked={eventFilterActive} />
              </div>
            ),
          },
        ],
      },
    ],
    [eventFilterActive]
  );

  const onFilterMenuClick = (item: MenuInfo) => {
    if (String(item.key) === EVENT_FILTER_KEY) {
      toggleEventFilter();
    }
  };

  return (
    <>
      <div className='flex min-w-0 items-center overflow-x-auto mt-1'>
        <div className='flex-shrink-0 [&_.truncate]:max-w-[30rem]'>
          <MultiFilter
            filterName='Event'
            filterKey={EVENT_FILTER_KEY}
            allItems={eventItems}
            selectedValues={selectedEvents}
            onToggleValue={toggleEvent}
            onClose={toggleEventFilter}
            active={eventFilterActive}
            disabled={disabled}
            overlayClassName='w-[28rem]'
          />
        </div>
      </div>

      <div
        role='button'
        className='bg-body hover:bg-waterLight flex h-5 flex-shrink-0 cursor-pointer items-center rounded-md mt-1'
      >
        <Dropdown
          onClick={onFilterMenuClick}
          items={filterMenuItems}
          trigger={['click']}
          disabled={disabled}
          className='h-full pl-1.5 pr-2.5'
        >
          <div
            data-testid='siteRecipientsEvents'
            className='flex items-center justify-center gap-1'
          >
            <Filter height={20} width={20} />

            <div className='text-1420 text-black whitespace-nowrap'>
              Event Filter
            </div>
          </div>
        </Dropdown>
      </div>
    </>
  );
};
